import { Component } from '@theme/component';

/**
 * Collection Menu Web Component
 *
 * Horizontal scrollable sub-category nav. A Custom Element instead of a
 * plain inline <script> specifically because connectedCallback() fires
 * reliably no matter how the element enters the DOM — including when a
 * section is patched in via innerHTML (Shopify theme-dev hot reload, morph-
 * based re-renders) — whereas a literal <script> tag inserted that way
 * never executes at all.
 *
 * Centers the active link on load — ported from the old theme's
 * plp-header.liquid centerActiveElement(), same formula. The browser clamps
 * scrollLeft to the valid range on its own, so when the active link is the
 * last item (e.g. the self-link fallback in _collection--menu.liquid, always
 * appended last) the centering math naturally overshoots past the max and
 * lands flush at the true end instead — no special-casing needed for that.
 * The list is hidden until positioning is applied
 * (.collection-menu__list--ready) so the unscrolled start position is never
 * visible even briefly.
 *
 * @extends {Component}
 */
class CollectionMenu extends Component {
	/** @type {HTMLElement|null} */
	#list = null;

	/** @type {HTMLElement|null} */
	#indicator = null;

	/** @type {HTMLElement|null} */
	#activeLink = null;

	connectedCallback() {
		super.connectedCallback();

		this.#list = this.querySelector('.collection-menu__list');
		if (!this.#list) return;

		this.#indicator = this.#list.querySelector('.collection-menu__indicator');
		this.#activeLink = this.#list.querySelector('.collection-menu__link--active');

		// try/finally so the list always ends up visible even if something
		// above throws unexpectedly, rather than staying hidden forever.
		try {
			this.#centerActiveElement();
			if (this.#indicator) this.#moveIndicator(this.#activeLink, false);
		} finally {
			this.#list.classList.add('collection-menu__list--ready');
		}

		if (this.#indicator) {
			this.#list.addEventListener('pointerenter', this.#handlePointerEnter, true);
			this.#list.addEventListener('pointerleave', this.#handlePointerLeave);
			this.#list.addEventListener('scroll', this.#handleScroll, { passive: true });
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback();

		if (this.#list) {
			this.#list.removeEventListener('pointerenter', this.#handlePointerEnter, true);
			this.#list.removeEventListener('pointerleave', this.#handlePointerLeave);
			this.#list.removeEventListener('scroll', this.#handleScroll);
		}
		this.#list = null;
		this.#indicator = null;
		this.#activeLink = null;
	}

	/**
	 * Public: make `link` the active tab right now — before the page it points
	 * to has arrived. Called by collection--nav.js on tap so the menu reacts in
	 * the same frame; the fetched page then replaces this whole element with
	 * its own menu, already rendered with the right active link.
	 * @param {HTMLElement|null} link
	 */
	setActive(link) {
		if (!this.#list || !link || link === this.#activeLink) return;
		for (const el of this.#list.querySelectorAll('.collection-menu__link--active')) {
			el.classList.remove('collection-menu__link--active');
			el.removeAttribute('aria-current');
		}
		link.classList.add('collection-menu__link--active');
		link.setAttribute('aria-current', 'page');
		this.#activeLink = link;
		this.#moveIndicator(link, true);
	}

	/**
	 * Ported verbatim from the old theme's plp-header.liquid centerActiveElement().
	 */
	#centerActiveElement() {
		const parent = this.#list;
		const activeElement = this.#activeLink;
		if (!parent || !activeElement) return;

		const parentRect = parent.getBoundingClientRect();
		const activeRect = activeElement.getBoundingClientRect();
		const scrollPosition = activeRect.left - parentRect.left + activeRect.width / 2 - parentRect.width / 2;
		parent.scrollLeft += scrollPosition;
	}

	/**
	 * @param {HTMLElement|null} el
	 * @param {boolean} animate
	 */
	#moveIndicator(el, animate) {
		const indicator = this.#indicator;
		const list = this.#list;
		if (!indicator || !list) return;

		if (!el) {
			indicator.style.width = '0';
			return;
		}

		const listRect = list.getBoundingClientRect();
		const elRect = el.getBoundingClientRect();
		const x = elRect.left - listRect.left + list.scrollLeft;

		if (!animate) indicator.style.transition = 'none';
		indicator.style.width = `${elRect.width}px`;
		indicator.style.transform = `translateX(${x}px)`;
		if (!animate) {
			indicator.offsetHeight; // force reflow
			indicator.style.transition = '';
		}
	}

	/** @param {PointerEvent} event */
	#handlePointerEnter = (event) => {
		const link = /** @type {HTMLElement|null} */ (event.target)?.closest?.('.collection-menu__link');
		if (link) this.#moveIndicator(/** @type {HTMLElement} */ (link), true);
	};

	#handlePointerLeave = () => {
		this.#moveIndicator(this.#activeLink, true);
	};

	#handleScroll = () => {
		this.#moveIndicator(this.#activeLink, false);
	};
}

if (!customElements.get('collection-menu')) {
	customElements.define('collection-menu', CollectionMenu);
}
